import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  Table,
  Tag,
  Typography,
  Space,
  Spin,
  Empty,
  message,
} from 'antd';
import {
  UserOutlined,
  VideoCameraOutlined,
  FileTextOutlined,
  LineChartOutlined,
  CrownOutlined,
  RobotOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { adminAPI, usersAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, analyticsRes] = await Promise.all([
        adminAPI.getDashboard(),
        adminAPI.getAnalytics({ period: '7d' })
      ]);
      
      if (dashboardRes.data.success) {
        setData(dashboardRes.data.data);
      }
      if (analyticsRes.data.success) {
        setAnalytics(analyticsRes.data.data);
      }
    } catch (error) {
      message.error('获取数据失败，请检查后端服务是否运行');
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const recentUsersColumns = [
    { title: '用户名', dataIndex: 'username', key: 'username' },
    { title: '邮箱', dataIndex: 'email', key: 'email' },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={role === 'vip' ? 'gold' : role === 'admin' ? 'red' : 'blue'}>
          {role.toUpperCase()}
        </Tag>
      ),
    },
  ];

  const recentTradesColumns = [
    { title: '股票代码', dataIndex: 'symbol', key: 'symbol' },
    {
      title: '盈亏',
      dataIndex: 'pnlAmount',
      key: 'pnlAmount',
      render: (amount) => (
        <Text type={amount >= 0 ? 'success' : 'danger'}>
          {amount >= 0 ? '+' : ''}{amount?.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
        </Text>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'blue' : 'green'}>
          {status === 'active' ? '进行中' : '已平仓'}
        </Tag>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return <Empty description="暂无数据，请确保后端服务正常运行" />;
  }

  // Simplified: only VIP and regular users
  const roleColors = {
    user: '#1890ff',
    vip: '#faad14',
    admin: '#722ed1'
  };

  const roleLabels = {
    user: '普通用户',
    vip: 'VIP会员',
    admin: '管理员'
  };

  // Convert tier distribution to role distribution
  const vipDistribution = analytics?.tierDistribution?.map(item => ({
    name: roleLabels[item.tier || item._id] || item.tier || item._id,
    value: item.count,
    color: roleColors[item.tier || item._id] || '#1890ff'
  })) || [];

  const userGrowthData = analytics?.userGrowth?.map(item => ({
    date: item.date || item._id,
    users: item.count
  })) || [];

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <Title level={4}>控制台概览</Title>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总用户数"
              value={data?.users?.total || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <Text type="secondary">今日新增 +{data?.users?.newToday || 0}</Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="VIP会员"
              value={data?.users?.vip || 0}
              prefix={<CrownOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
            <Text type="secondary">
              占比 {data?.users?.total ? ((data.users.vip / data.users.total) * 100).toFixed(1) : 0}%
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总盈利"
              value={data?.trading?.totalProfit || 0}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#52c41a' }}
            />
            <Text type="secondary">活跃交易 {data?.trading?.activeTrades || 0}</Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="AI分析次数"
              value={data?.ai?.totalAnalyses || 0}
              prefix={<RobotOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
            <Text type="secondary">今日 +{data?.ai?.analysesToday || 0}</Text>
          </Card>
        </Col>
      </Row>

      {/* Content Stats */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="视频教程"
              value={data?.content?.videos || 0}
              prefix={<VideoCameraOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="文档资料"
              value={data?.content?.documents || 0}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="交易策略"
              value={data?.content?.strategies || 0}
              prefix={<LineChartOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="用户增长趋势">
            {userGrowthData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={userGrowthData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="users"
                    stroke="#1890ff"
                    strokeWidth={2}
                    dot={{ fill: '#1890ff' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="暂无数据" style={{ height: 300 }} />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="VIP会员分布">
            {vipDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={vipDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {vipDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="暂无数据" style={{ height: 300 }} />
            )}
          </Card>
        </Col>
      </Row>

      {/* Recent Data Tables */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="最近注册用户">
            <Table
              dataSource={data?.recent?.users || []}
              columns={recentUsersColumns}
              rowKey="id"
              pagination={false}
              size="small"
              locale={{ emptyText: '暂无数据' }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="最近交易记录">
            <Table
              dataSource={data?.recent?.trades || []}
              columns={recentTradesColumns}
              rowKey="id"
              pagination={false}
              size="small"
              locale={{ emptyText: '暂无数据' }}
            />
          </Card>
        </Col>
      </Row>
    </Space>
  );
};

export default Dashboard;
